/**
 * Onboarding gate for users who have signed in but don't yet belong to an
 * organization. The point of the gate is to keep regular staff from
 * accidentally clicking "create organization" and ending up with a duplicate
 * of their employer's tenant. We can't merge tenants after the fact, so this
 * page is the cheap forcing-function: pick a role, see different copy.
 *
 * Flow:
 *   - signed out         → bounce to home
 *   - signed in + orgId  → bounce to /admin (they have an org already)
 *   - signed in, no org  → role gate (owner vs staff)
 *       owner   → strong warning + Clerk's <CreateOrganization />
 *       staff   → "wait for an invite" with sign-out escape hatch
 */

import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { CreateOrganization, SignOutButton } from '@clerk/react';
import { Building2, UserRound, ArrowRight, AlertTriangle } from 'lucide-react';
import { useClerkReady } from '../lib/useClerkReady';
import AuthUnavailable from '../components/AuthUnavailable';

type Choice = 'owner' | 'staff' | null;

export default function Welcome() {
  const { isLoaded, isSignedIn, orgId, timedOut } = useClerkReady();
  const [choice, setChoice] = useState<Choice>(null);

  if (!isLoaded) return timedOut ? <AuthUnavailable /> : <p className="muted">Loading…</p>;

  if (!isSignedIn) {
    return (
      <div className="card">
        <h1>Sign in first</h1>
        <p>
          You need to sign in or create an account before continuing. Head back
          to the <Link to="/">homepage</Link> and use the Sign up / Sign in
          buttons in the top right.
        </p>
      </div>
    );
  }

  if (orgId) return <Navigate to="/admin" replace />;

  if (choice === null) return <RoleGate onPick={setChoice} />;
  if (choice === 'staff') return <StaffWaiting onBack={() => setChoice(null)} />;
  return <OwnerPath onBack={() => setChoice(null)} />;
}

function RoleGate({ onPick }: { onPick: (c: Exclude<Choice, null>) => void }) {
  return (
    <div>
      <h1>Welcome to Invitey</h1>
      <p className="muted" style={{ maxWidth: '34rem' }}>
        Tell us who you are. Picking wrong creates a duplicate organization we
        can&rsquo;t easily merge later.
      </p>

      <div className="role-grid">
        <button
          type="button"
          className="role-card role-card--admin"
          onClick={() => onPick('owner')}
        >
          <span className="role-card__icon" aria-hidden="true">
            <Building2 size={22} strokeWidth={2} />
          </span>
          <h3>I own or manage this business</h3>
          <p>I&rsquo;m setting Invitey up for my company. No one here has done it yet.</p>
          <span className="role-cta">
            Continue as admin <ArrowRight size={15} strokeWidth={2.5} />
          </span>
        </button>

        <button
          type="button"
          className="role-card role-card--staff"
          onClick={() => onPick('staff')}
        >
          <span className="role-card__icon" aria-hidden="true">
            <UserRound size={22} strokeWidth={2} />
          </span>
          <h3>I&rsquo;m a staff member</h3>
          <p>Customers book time with me. Someone else runs our Invitey account.</p>
          <span className="role-cta">
            Wait for an invite <ArrowRight size={15} strokeWidth={2.5} />
          </span>
        </button>
      </div>

      <p className="muted small" style={{ marginTop: '1.5rem' }}>
        Not sure? Choose staff &mdash; the worst case is a short wait for your invite.
      </p>
    </div>
  );
}

function StaffWaiting({ onBack }: { onBack: () => void }) {
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Wait for your invitation</h2>
      <p>
        Staff join through an invite from their organization&rsquo;s admin. Ask
        whoever set up Invitey to invite this email address, then sign back in.
      </p>
      <div className="row" style={{ marginTop: '1rem' }}>
        <button type="button" onClick={onBack}>
          &larr; Back
        </button>
        <span className="spacer" />
        <SignOutButton>
          <button type="button">Sign out</button>
        </SignOutButton>
      </div>
    </div>
  );
}

function OwnerPath({ onBack }: { onBack: () => void }) {
  return (
    <div>
      <div className="card warning-card">
        <h2 style={{ marginTop: 0 }} className="warning-card__title">
          <AlertTriangle size={18} strokeWidth={2.25} aria-hidden="true" />
          Read this before continuing
        </h2>
        <p>
          <strong>Only create an organization if you own or run this business.</strong>{' '}
          If someone told you to use Invitey, ask them for an invite instead
          &mdash; a duplicate organization splits your bookings and can&rsquo;t
          be merged.
        </p>
        <p style={{ marginBottom: 0 }}>
          <button type="button" onClick={onBack}>
            &larr; I&rsquo;m actually a staff member
          </button>
        </p>
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Create your organization</h2>
        <CreateOrganization afterCreateOrganizationUrl="/admin" skipInvitationScreen />
      </div>
    </div>
  );
}
